import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Stack } from '@mui/material';
import Alert from '@mui/material/Alert';

import type {
  CodingResultsSubtopic,
  CodingResultSubTopicTotal,
} from 'src/types/dashboard';
import renderInsightWheel from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/InsightWheel/chart';
import {
  addEventListener,
  removeEventListener,
} from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/InsightWheel/helper';
import type { SentimentFilter } from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/types';

import { styles } from './index.styles';
import { styles as insightWheelWidgetStyles } from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/index.styles';

export const INSIGHT_WHEEL_TEST_ID = 'insight-wheel-test-id';

export interface InsightWheelProps {
  selectedSubtopics: CodingResultsSubtopic[] | CodingResultSubTopicTotal[];
  valueFromSlider: number;
  sentimentFilter: SentimentFilter;
}

const InsightWheel: FC<InsightWheelProps> = ({
  selectedSubtopics,
  valueFromSlider,
  sentimentFilter,
}) => {
  const container = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('dashboard');

  const sortedSubtopics = selectedSubtopics
    // make sure the biggest topics make it into the selection
    .sort(({ count: countA }, { count: countB }) => countB - countA)
    .slice(0, valueFromSlider)
    // sort the topics by color for a nicer look
    .sort(({ color: colorA }, { color: colorB }) =>
      colorA < colorB ? -1 : colorA > colorB ? 1 : 0,
    );

  useEffect(() => {
    renderInsightWheel(container.current, sortedSubtopics);
    const eventListener =
      container.current && addEventListener(container.current, sortedSubtopics);
    return () => {
      eventListener && removeEventListener(eventListener);
    };
  }, [container, JSON.stringify(sortedSubtopics), sentimentFilter]);

  return (
    <Box sx={styles.insightWheel} data-test-id={INSIGHT_WHEEL_TEST_ID}>
      <Grid sx={styles.svgCanvas}>
        <Stack direction="column" sx={{ height: '100%' }}>
          <Stack
            direction="column"
            ref={container}
            sx={[
              { flexGrow: 1, justifyContent: 'center' },
              styles.svgContainer,
              !sortedSubtopics.length && styles.hide,
            ].filter(Boolean)}
          />
          <Stack
            direction="column"
            sx={[!sortedSubtopics.length && styles.svgCanvas].filter(Boolean)}
          >
            <Alert
              severity="error"
              icon={false}
              sx={[
                insightWheelWidgetStyles.topicHint,
                ...(sortedSubtopics.length ? [styles.hide] : []),
              ]}
            >
              {t('InsightWheel.TopicHint')}
            </Alert>
          </Stack>
          <Alert
            severity="error"
            icon={false}
            sx={insightWheelWidgetStyles.linkageHint}
          >
            {t('InsightWheel.LinkageHint')}
          </Alert>
        </Stack>
      </Grid>
    </Box>
  );
};

export default InsightWheel;
